import type { CSSProperties } from 'react';
import rgba from 'polished/lib/color/rgba';
import styled, { createGlobalStyle, css } from 'styled-components';

import { Breakpoint } from '@tmh-website/shared/const';
import { Color } from '@tmh-website/shared/colors';

import { ThemeType } from './theme';

/**
 * This variable is needed to recalculate word lengths on each breakpoint.
 * Mainly used for attaching the AnimatedLine to text.
 */
const textBreakpoints = [Breakpoint.Md, Breakpoint.Lg, Breakpoint.Xl];

const headlineCss = css`
  display: block;
  margin: 1em 0 0.6em;
  font-family: ${props => props.theme.fontFamily.headlines};
  font-weight: ${props => props.theme.fontWeight.bold};
  hyphens: manual;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const h1Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h1Mobile};
  line-height: ${props => props.theme.lineHeight.small};
  letter-spacing: ${props => props.theme.letterSpacing.tight};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.h1Tablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.h1};
  }
`;

const h2Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h2Mobile};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.tight};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.h2Tablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.h2};
  }
`;

const h3Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h3Mobile};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.tight};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.h3Tablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.h3};
  }
`;

const h4Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h4Mobile};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.tight};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.h4Tablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.h4};
  }
`;

const h5Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h5Mobile};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.h5Tablet};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    font-size: ${props => props.theme.fontSize.h5};
  }
`;

const h6Css = css`
  ${headlineCss};
  font-size: ${props => props.theme.fontSize.h6};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const copyBaseCss = css`
  font-family: ${props => props.theme.fontFamily.body};
  font-weight: ${props => props.theme.fontWeight.regular};
  line-height: ${props => props.theme.lineHeight.large};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const copyCss = css`
  ${copyBaseCss};
  font-size: ${props => props.theme.fontSize.copyMobile};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.copyTablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.copy};
  }
`;

const copyBigCss = css`
  ${copyBaseCss};
  font-size: ${props => props.theme.fontSize.copyBigMobile};
  font-weight: ${props => props.theme.fontWeight.medium};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.copyBigTablet};
  }
  @media (${props => props.theme.breakpoint.xl.up}) {
    font-size: ${props => props.theme.fontSize.copyBig};
  }
`;

const uiTextCss = css`
  font-family: ${props => props.theme.fontFamily.body};
  font-size: ${props => props.theme.fontSize.uiText};
  font-weight: ${props => props.theme.fontWeight.medium};
  line-height: ${props => props.theme.lineHeight.large};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const uiTextBoldCss = css`
  font-family: ${props => props.theme.fontFamily.body};
  font-size: ${props => props.theme.fontSize.uiText};
  font-weight: ${props => props.theme.fontWeight.bold};
  line-height: ${props => props.theme.lineHeight.large};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const uiTextSmallCss = css`
  font-family: ${props => props.theme.fontFamily.body};
  font-size: ${props => props.theme.fontSize.uiTextSmall};
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: ${props => props.theme.lineHeight.large};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const uiTextSuperSmallCss = css`
  font-family: ${props => props.theme.fontFamily.body};
  font-size: ${props => props.theme.fontSize.uiTextSuperSmallCss};
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
`;

const statementBigCss = css`
  font-family: ${props => props.theme.fontFamily.headlines};
  font-size: ${props => props.theme.fontSize.statementBigMobile};
  font-weight: ${props => props.theme.fontWeight.bold};
  line-height: ${props => props.theme.lineHeight.medium};
  letter-spacing: ${props => props.theme.letterSpacing.normal};
  @media (${props => props.theme.breakpoint.md.up}) {
    font-size: ${props => props.theme.fontSize.statementBigTablet};
  }
  @media (${props => props.theme.breakpoint.lg.up}) {
    font-size: ${props => props.theme.fontSize.statementBig};
  }
`;

const GlobalFontStyles = createGlobalStyle<{ theme?: ThemeType }>`
  body {
    font-family: ${props => props.theme.fontFamily.body};
    font-size: ${props => props.theme.fontSize.body};
    font-weight: ${props => props.theme.fontWeight.regular};
    line-height: ${props => props.theme.lineHeight.large};
    color: ${props => props.theme.color[Color.Blue_1000_Deep]};
    letter-spacing: ${props => props.theme.letterSpacing.normal};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    // Adjust the fallback font to be closer to the real font
    &.fonts-are-loading {
      h1,
      h2,
      h3,
      h4 {
        line-height: 1.33;
        letter-spacing: 0.075em;
        word-spacing: -0.1em;
      }

      .hero-headline {
        font-size: calc(${props => props.theme.fontSize.h1Mobile} - 6.5px);
        @media (${props => props.theme.breakpoint.md.up}) {
          font-size: calc(${props => props.theme.fontSize.h1Tablet} - 8px);
        }
        @media (${props => props.theme.breakpoint.xl.up}) {
          font-size: calc(${props => props.theme.fontSize.h1} - 10px);
        }
      }
    }
  }


  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${headlineCss};
  }

  p {
    margin: 0.5em 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1 {
    ${h1Css}
  }

  h2 {
    ${h2Css}
  }

  h3 {
    ${h3Css}
  }

  h4 {
    ${h4Css}
  }

  h5 {
    ${h5Css}
  }
  h6 {
    ${h6Css}
  }

  sub {
    ${uiTextSmallCss};
    display: inline-block;
    font-weight: ${props => props.theme.fontWeight.medium};
    transform: translateY(-6%) !important;
  }

  a {
    font-weight: ${props => props.theme.fontWeight.medium};
    color: ${props => props.theme.color[Color.Blue_500_Ultramarine]};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  input, select {
    font-family: ${props => props.theme.fontFamily.body};
  }

  address {
    font-style: inherit;
  }
`;

export const articleHeadlinesCss = css`
  /**
   * We adjust the headline size on desktop a little bit
   */
  h2 {
    font-size: 24px;
    @media (${props => props.theme.breakpoint.xl.up}) {
      font-size: 32px;
    }
  }

  h3 {
    @media (${props => props.theme.breakpoint.xl.up}) {
      font-size: 24px;
    }
  }
`;

export type FontStyles =
  | 'copy'
  | 'copyBig'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'uiText'
  | 'uiTextBold'
  | 'uiTextSmall'
  | 'uiTextSuperSmall'
  | 'statementBig'
  | 'label';

export interface TypographyWithoutOpacityProps {
  $color?: Color;
  $opacity?: never;
  $style: FontStyles;
  $noMargin?: boolean;
  $noMarginTop?: boolean;
  $noMarginBottom?: boolean;
  $textAlign?: 'left' | 'center' | 'right';
  $increaseReadability?: boolean;
  $textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
  $fontWeight?: CSSProperties['fontWeight'];
}

export interface TypographyWithOpacityProps extends Omit<TypographyWithoutOpacityProps, '$color' | '$opacity'> {
  $color: Color;
  $opacity: number;
}

export type TypographyProps = TypographyWithoutOpacityProps | TypographyWithOpacityProps;

const Typography = styled.span<TypographyProps>`
  ${props => (props.$style === 'copy' ? copyCss : '')};
  ${props => (props.$style === 'copyBig' ? copyBigCss : '')};
  ${props => (props.$style === 'h1' ? h1Css : '')};
  ${props => (props.$style === 'h2' ? h2Css : '')};
  ${props => (props.$style === 'h3' ? h3Css : '')};
  ${props => (props.$style === 'h4' ? h4Css : '')};
  ${props => (props.$style === 'h5' ? h5Css : '')};
  ${props => (props.$style === 'h6' ? h6Css : '')};
  ${props => (props.$style === 'uiText' ? uiTextCss : '')};
  ${props => (props.$style === 'uiTextBold' ? uiTextBoldCss : '')};
  ${props => (props.$style === 'uiTextSmall' ? uiTextSmallCss : '')};
  ${props => (props.$style === 'uiTextSuperSmall' ? uiTextSuperSmallCss : '')};
  ${props => (props.$style === 'statementBig' ? statementBigCss : '')};
  ${props => props.$noMargin && `margin: 0`};
  ${props => props.$noMarginTop && `margin-top: 0`};
  ${props => props.$noMarginBottom && `margin-bottom: 0`};
  ${props => props.$textAlign && `text-align: ${props.$textAlign}`};
  ${props => props.$color !== undefined && props.$opacity === undefined && `color: ${props.theme.color[props.$color]}`};
  ${props =>
    props.$opacity !== undefined &&
    !props.$increaseReadability &&
    `color: ${rgba(props.theme.color[props.$color], props.$opacity)};`};
  ${props => props.$increaseReadability && `text-shadow: 0 0 4px rgba(0, 0, 0, 0.6);`};
  ${props => props.$textTransform && `text-transform: ${props.$textTransform}`};
  ${props => props.$fontWeight && `font-weight: ${props.$fontWeight}`};
`;

const StyledTypo = {
  headlineCss,
  h1Css,
  h2Css,
  h3Css,
  h4Css,
  h5Css,
  h6Css,
  copyBigCss,
  copyCss,
  uiTextCss,
  uiTextBoldCss,
  uiTextSmallCss,
  uiTextSuperSmallCss,
  statementBigCss,
  GlobalFontStyles,
  Typography,
  textBreakpoints,
};

export default StyledTypo;
